import React, { DragEvent, useMemo, useState } from 'react';
import { ChevronRight, GripVertical, Plus } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { getNodeIconColor, getNodeStyling } from '@/constants/node-styling';
import {
  getDeprecationMessage,
  isNodeDeprecated,
  TOOLTIP_DELAY_MS,
} from '@/constants/deprecated-nodes';
import { DEFAULT_EXPANDED_MCP_SERVERS } from '@/constants/node-catalog';
import { cn } from '@/lib/utils';
import { plainNodeDescription } from '@/pages/workflow/helper/catalog-copy';
import { NodeCategory, NodeStruct } from '@/types/node-types';

import { IconTile } from '../icon-tile';

interface AddNodesListProps {
  nodesByCategory: Record<string, NodeStruct[]>;
  categories: string[];
  openCategories: string[];
  onToggleCategory: (category: string) => void;
  onDragStart: (event: DragEvent<HTMLDivElement>, node: NodeStruct) => void;
  onAddNode: (node: NodeStruct) => void;
  isLocked?: boolean;
  search?: string;
}

const NodeRow: React.FC<{
  node: NodeStruct;
  onDragStart: (event: DragEvent<HTMLDivElement>, node: NodeStruct) => void;
  onAddNode: (node: NodeStruct) => void;
  isLocked: boolean;
}> = ({ node, onDragStart, onAddNode, isLocked }) => {
  const [isAddButtonHovered, setIsAddButtonHovered] = useState(false);
  const iconColor = getNodeIconColor(node.node_type, node.icon_color);
  const useThemeColors = iconColor === null;
  const label = node.user_config_name || node.display_name;
  const isDeprecated = isNodeDeprecated(node.display_name);
  const deprecationMessage = getDeprecationMessage(node.display_name);
  // --- Phase 6: catalog text for people (no implementation jargon) ---
  const description =
    (isDeprecated && deprecationMessage) || plainNodeDescription(node);
  // --- end Phase 6 ---
  const addLabel = isLocked
    ? 'Workflow is locked'
    : `Add ${node.display_name} to canvas`;
  const tooltipText = isAddButtonHovered ? addLabel : description;

  const handleAddClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!isLocked) onAddNode(node);
  };

  return (
    <Tooltip delayDuration={TOOLTIP_DELAY_MS}>
      <TooltipTrigger asChild>
        <div
          draggable={!isLocked}
          onDragStart={(event) => onDragStart(event, node)}
          onDoubleClick={() => onAddNode(node)}
          className={cn(
            'group flex w-full items-center gap-2 rounded-md bg-accent py-2 pr-3 pl-2 text-left',
            'transition-colors hover:bg-accent/70',
            isLocked ? 'cursor-not-allowed opacity-60' : 'cursor-grab',
          )}
        >
          <IconTile
            icon={node.icon}
            backgroundColor={iconColor || ''}
            iconColor={iconColor ? '#ffffff' : ''}
            size='small'
            className={cn('size-7', useThemeColors && 'bg-slate-500')}
            iconClassName={cn('w-5! h-5!', useThemeColors && 'text-white')}
          />
          <span className='min-w-0 flex-1 truncate text-sm text-foreground'>
            {label}
          </span>
          {isDeprecated ? (
            <Badge
              variant='secondary'
              className='shrink-0 bg-red-100 text-xs text-red-600'
            >
              Deprecated
            </Badge>
          ) : (
            node.beta && (
              <Badge variant='secondary' className='shrink-0 text-xs'>
                Beta
              </Badge>
            )
          )}
          <Button
            variant='ghost'
            size='icon'
            disabled={isLocked}
            aria-label={addLabel}
            onClick={handleAddClick}
            onDoubleClick={(event) => event.stopPropagation()}
            onMouseEnter={() => setIsAddButtonHovered(true)}
            onMouseLeave={() => setIsAddButtonHovered(false)}
            onFocus={() => setIsAddButtonHovered(true)}
            onBlur={() => setIsAddButtonHovered(false)}
            className='size-6 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100'
          >
            <Plus className='size-4' />
          </Button>
          <GripVertical className='size-4 shrink-0 text-muted-foreground' />
        </div>
      </TooltipTrigger>
      {tooltipText && (
        <TooltipContent side='left' className='max-w-xs'>
          {tooltipText}
        </TooltipContent>
      )}
    </Tooltip>
  );
};

const ToolServerGroups: React.FC<{
  nodes: NodeStruct[];
  search: string;
  onDragStart: (event: DragEvent<HTMLDivElement>, node: NodeStruct) => void;
  onAddNode: (node: NodeStruct) => void;
  isLocked: boolean;
}> = ({ nodes, search, onDragStart, onAddNode, isLocked }) => {
  const [openServers, setOpenServers] = useState<string[]>(
    DEFAULT_EXPANDED_MCP_SERVERS,
  );

  const { servers, ungrouped } = useMemo(() => {
    const grouped: Record<string, NodeStruct[]> = {};
    const loose: NodeStruct[] = [];
    nodes.forEach((node) => {
      if (node.tool_category) {
        grouped[node.tool_category] = grouped[node.tool_category] ?? [];
        grouped[node.tool_category].push(node);
      } else {
        loose.push(node);
      }
    });
    return { servers: grouped, ungrouped: loose };
  }, [nodes]);

  const toggleServer = (server: string) =>
    setOpenServers((previous) =>
      previous.includes(server)
        ? previous.filter((name) => name !== server)
        : [...previous, server],
    );

  return (
    <>
      {Object.keys(servers)
        .sort()
        .map((server) => {
          const serverNodes = servers[server];
          const isOpen = search.length > 0 || openServers.includes(server);
          const representative = serverNodes[0];
          const iconColor = getNodeIconColor(
            representative.node_type,
            representative.icon_color,
          );

          return (
            <div
              key={server}
              className={cn(
                'flex flex-col gap-1 rounded-md',
                isOpen && 'ring-1 ring-border',
              )}
            >
              <button
                type='button'
                onClick={() => toggleServer(server)}
                aria-expanded={isOpen}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md py-2 pr-3 pl-2 text-left transition-colors',
                  isOpen
                    ? 'bg-transparent hover:bg-accent/40'
                    : 'bg-accent hover:bg-accent/70',
                )}
              >
                <IconTile
                  icon={representative.icon}
                  backgroundColor={iconColor || ''}
                  iconColor={iconColor ? '#ffffff' : ''}
                  size='small'
                  className={cn('size-7', iconColor === null && 'bg-slate-500')}
                  iconClassName={cn(
                    'w-5! h-5!',
                    iconColor === null && 'text-white',
                  )}
                />
                <span className='min-w-0 flex-1 truncate text-sm text-foreground'>
                  {server}
                </span>
                <span className='shrink-0 text-xs text-muted-foreground'>
                  {serverNodes.length}{' '}
                  {serverNodes.length === 1 ? 'tool' : 'tools'}
                </span>
                <ChevronRight
                  className={cn(
                    'size-4 shrink-0 text-muted-foreground transition-transform',
                    isOpen && 'rotate-90',
                  )}
                />
              </button>

              {isOpen && (
                <div className='flex flex-col gap-1 px-2 pb-2'>
                  {serverNodes.map((node) => (
                    <NodeRow
                      key={node.node_id}
                      node={node}
                      onDragStart={onDragStart}
                      onAddNode={onAddNode}
                      isLocked={isLocked}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}

      {ungrouped.map((node) => (
        <NodeRow
          key={node.node_id}
          node={node}
          onDragStart={onDragStart}
          onAddNode={onAddNode}
          isLocked={isLocked}
        />
      ))}
    </>
  );
};

export const AddNodesList: React.FC<AddNodesListProps> = ({
  nodesByCategory,
  categories,
  openCategories,
  onToggleCategory,
  onDragStart,
  onAddNode,
  isLocked = false,
  search = '',
}) => (
  <div className='flex flex-col gap-1 px-4'>
    {categories.map((category) => {
      // A search opens every group that still has matches, so a hit never
      // hides behind a collapsed header.
      const isOpen = search.length > 0 || openCategories.includes(category);
      const nodes = nodesByCategory[category] || [];
      const { label: categoryLabel } = getNodeStyling(category as NodeCategory);

      return (
        <div key={category} className='flex flex-col gap-1'>
          <button
            type='button'
            onClick={() => onToggleCategory(category)}
            aria-expanded={isOpen}
            className='flex h-8 w-full items-center gap-2 rounded-md p-2 text-left transition-colors hover:bg-accent/60'
          >
            <span className='min-w-0 flex-1 truncate text-sm leading-none text-sidebar-foreground'>
              {categoryLabel}
            </span>
            <ChevronRight
              className={cn(
                'size-4 shrink-0 text-muted-foreground transition-transform',
                isOpen && 'rotate-90',
              )}
            />
          </button>

          {isOpen &&
            (category === NodeCategory.TOOL ? (
              // MCP servers collapse into their own subgroups rather than
              // spilling every tool into the top level.
              <ToolServerGroups
                nodes={nodes}
                search={search}
                onDragStart={onDragStart}
                onAddNode={onAddNode}
                isLocked={isLocked}
              />
            ) : (
              nodes.map((node) => (
                <NodeRow
                  key={node.node_id}
                  node={node}
                  onDragStart={onDragStart}
                  onAddNode={onAddNode}
                  isLocked={isLocked}
                />
              ))
            ))}
        </div>
      );
    })}
  </div>
);
